import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Modal, ModalBody } from "reactstrap";
import { fetchPushPublicKey } from "../redux/slices/notificationsSlice";
import usePushEnable from "../hooks/usePushEnable";
import {
  dismissPushNudge,
  isPushNudgeSuppressed,
  markPushNudgeShownThisSession,
  wasPushNudgeShownThisSession,
} from "../utils/pushNudge";

// Code review F5 — id of the focus target rendered by src/routes/index.js's <main>. Moving
// focus there on dismiss/auto-close (before this component unmounts) keeps keyboard/screen
// reader focus from falling back to <body>.
const MAIN_CONTENT_ID = "wt-main-content";

// PUSH-NUDGE-001/002 — post-login soft prompt, now presented as a centered modal dialog
// rather than a full-width strip under the header (PUSH-NUDGE-002: "this looks ugly").
// Mounted once from the same spot (src/routes/index.js) whenever the user is signed in.
// Reuses usePushEnable (src/hooks/usePushEnable.js) for the actual "ask + subscribe"
// mechanics — the browser permission prompt is STILL only ever triggered from this
// component's own Enable button click, never on load or from an effect; Firefox/Safari
// refuse a background-triggered prompt outright, and a surprise "Block" is permanent, so
// that rule holds here exactly as it does in PushSettings.js.
export const isIOSDevice = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  // iPadOS 13+ masquerades as desktop Safari on Mac — the touch-point check is the standard
  // way to tell an iPad apart from an actual Mac.
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

export const isStandaloneDisplay = () =>
  window.navigator.standalone === true ||
  window.matchMedia("(display-mode: standalone)").matches;

// A modal shouldn't linger the way the old banner did (4s) — 1.5s is enough to register
// "done" without blocking the user from getting back to the page.
const SUCCESS_MESSAGE_MS = 1500;

// Delay opening the dialog by ~1s after mount so it doesn't collide with the page's own
// initial loading overlay right after login/navigation.
const OPEN_DELAY_MS = 1000;

// The accessible name stays fixed regardless of which variant/marketing copy is showing —
// see the `labelledBy` usage below for why this is a dedicated (visually hidden) title
// rather than the visible headline itself.
const ENABLE_DIALOG_NAME = "Enable push notifications";
const SUCCESS_DIALOG_NAME = "Push notifications enabled";
const ENABLE_TITLE_ID = "wt-push-nudge-modal-title";
const SUCCESS_TITLE_ID = "wt-push-nudge-modal-success-title";

const BellIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path
      d="M12 3a5 5 0 0 0-5 5v3.19c0 .68-.24 1.34-.68 1.86L4.6 15.1c-.86 1-.13 2.55 1.18 2.55h12.44c1.31 0 2.04-1.55 1.18-2.55l-1.72-2.05a2.9 2.9 0 0 1-.68-1.86V8a5 5 0 0 0-5-5Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path d="M9.5 19a2.5 2.5 0 0 0 5 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const CheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path
      d="M5 12.5l4.5 4.5L19 7.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PushNudgeBanner = () => {
  const dispatch = useDispatch();
  const { supported, pushConfigured, pushPermission, subscribed, subscribing, error, enable } =
    usePushEnable();

  // Read once at mount, like `supported` inside usePushEnable itself — these never change
  // over a page's lifetime, and computing them up front avoids a first-paint flash of the
  // wrong variant.
  const [isIOS] = useState(isIOSDevice);
  const [standalone] = useState(isStandaloneDisplay);
  const [dismissed, setDismissed] = useState(
    () => isPushNudgeSuppressed() || wasPushNudgeShownThisSession()
  );
  const [delayElapsed, setDelayElapsed] = useState(false);
  const [justEnabled, setJustEnabled] = useState(false);
  const enableClickedRef = useRef(false);
  const hideTimerRef = useRef(null);
  const openTimerRef = useRef(null);

  // usePushEnable's own public-key fetch is gated on `supported` (PushSettings relies on
  // that to never call the endpoint for an unsupported browser). iOS Safari running as a
  // plain browser tab (not installed to the Home Screen) has no serviceWorker/PushManager,
  // so `supported` is false there — but the Home-Screen-instructions variant below still
  // needs to know whether push is configured server-side at all (a server with no VAPID
  // keys hides that variant too), so fetch it here for exactly the case the shared hook
  // skips.
  useEffect(() => {
    if (!supported) {
      dispatch(fetchPushPublicKey());
    }
  }, [dispatch, supported]);

  // ~1s open delay (see OPEN_DELAY_MS above) — purely a presentation gate, independent of
  // the eligibility checks below.
  useEffect(() => {
    openTimerRef.current = setTimeout(() => setDelayElapsed(true), OPEN_DELAY_MS);
    return () => clearTimeout(openTimerRef.current);
  }, []);

  useEffect(
    () => () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    },
    []
  );

  // Focus a sensible target (the main content landmark) before this dialog closes — otherwise
  // dismissing it (click or auto-close) leaves focus on a node that's about to disappear,
  // which most browsers then silently drop to <body> (F5).
  const focusMainContent = () => {
    document.getElementById(MAIN_CONTENT_ID)?.focus();
  };

  // Fires only after this dialog's own Enable click, never for a subscription discovered to
  // already exist (that case is handled by the `!subscribed` show-condition below, which
  // simply keeps the dialog from ever opening in the first place).
  //
  // Marking the nudge "enabled forever" (`markPushNudgeEnabled`) happens inside
  // `usePushEnable`'s `enable()` itself (code review F1) rather than here, so it fires
  // regardless of which UI performed the enable — the bell dropdown's PushSettings, or this
  // dialog. This effect only owns the dialog's own success-message UX.
  useEffect(() => {
    if (!enableClickedRef.current || subscribing) {
      return;
    }
    if (subscribed && !error) {
      enableClickedRef.current = false;
      setJustEnabled(true);
      hideTimerRef.current = setTimeout(() => {
        setDismissed(true);
        focusMainContent();
      }, SUCCESS_MESSAGE_MS);
    } else if (error) {
      // Code review F11 — clear the "click in flight" ref on failure too, not just on
      // success, so a retry after a failed enable isn't mistaken for a click this effect
      // already handled.
      enableClickedRef.current = false;
    }
  }, [subscribed, subscribing, error]);

  const handleEnableClick = () => {
    enableClickedRef.current = true;
    enable();
  };

  // Shared by "Not now" (enable/desktop variant) and "Got it" (iOS variant) — both are the
  // same 14-day dismissal, just worded differently for the two contexts.
  const handleDismiss = () => {
    dismissPushNudge();
    markPushNudgeShownThisSession();
    setDismissed(true);
    focusMainContent();
  };

  const isIOSHomeScreenPrompt = isIOS && !standalone;

  // (a)/(c) — desktop, Android, or iOS already installed to the Home Screen (iOS 16.4+
  // exposes PushManager there, so it takes this same path as desktop/Android).
  const showEnableVariant =
    !isIOSHomeScreenPrompt &&
    supported &&
    pushConfigured === true &&
    pushPermission !== null &&
    pushPermission !== "denied" &&
    !subscribed;

  // (b) — iOS/iPadOS Safari, not installed. Never offered if the server has no push
  // configured at all (contract §6's "hide all push UI" rule applies here too).
  const showIOSVariant = isIOSHomeScreenPrompt && pushConfigured === true;

  // What content the dialog would show, independent of whether it's actually allowed to be
  // open yet (dismissed / still within the open delay) — kept separate from `isOpen` so the
  // "nothing eligible" case (`variant === null`) can bail out to `null` before ever touching
  // reactstrap's <Modal>.
  let variant = null;
  if (justEnabled) {
    variant = "success";
  } else if (showEnableVariant) {
    variant = "enable";
  } else if (showIOSVariant) {
    variant = "ios";
  }

  if (!variant) {
    return null;
  }

  const isOpen = !dismissed && delayElapsed;

  return (
    <Modal
      isOpen={isOpen}
      centered
      backdrop="static"
      keyboard={false}
      fade={false}
      returnFocusAfterClose={false}
      labelledBy={variant === "success" ? SUCCESS_TITLE_ID : ENABLE_TITLE_ID}
      className="wt-push-nudge-modal"
    >
      <ModalBody>
        {variant === "success" && (
          <>
            {/* The accessible dialog name stays "Push notifications enabled" regardless of
                copy tweaks to the visible confirmation below — see the module-level comment
                on ENABLE_DIALOG_NAME. */}
            <h2 id={SUCCESS_TITLE_ID} className="wt-push-nudge-modal-sr-only">
              {SUCCESS_DIALOG_NAME}
            </h2>
            <div className="wt-push-nudge-modal-icon wt-push-nudge-modal-icon--success">
              <CheckIcon />
            </div>
            {/* Code review F4 — `role="status"` so screen readers announce the confirmation
                (a live region) without it being flagged as an error/alert. */}
            <p className="wt-push-nudge-modal-success" role="status">
              Notifications are on.
            </p>
          </>
        )}

        {variant === "enable" && (
          <>
            {/* aria-labelledby (via `labelledBy` above) is what actually names the dialog —
                pointing it at a dedicated, visually hidden title (rather than the visible
                marketing headline just below) keeps the accessible name stable as
                "Enable push notifications" across copy changes and the iOS variant, so
                existing consumers/selectors of that name keep working. */}
            <h2 id={ENABLE_TITLE_ID} className="wt-push-nudge-modal-sr-only">
              {ENABLE_DIALOG_NAME}
            </h2>
            <div className="wt-push-nudge-modal-icon">
              <BellIcon />
            </div>
            <p className="wt-push-nudge-modal-title">
              Get alerted when a timesheet needs you
            </p>
            <p className="wt-push-nudge-modal-body">
              Turn on browser notifications and we&apos;ll ping you when a timesheet is
              submitted, returned or ready for approval — even when WebTime isn&apos;t open.
            </p>
            <div className="wt-push-nudge-modal-actions">
              <button
                type="button"
                className="primary-btn wt-push-nudge-modal-btn"
                onClick={handleEnableClick}
                disabled={subscribing}
              >
                {subscribing ? "Enabling…" : "Enable notifications"}
              </button>
              <button
                type="button"
                className="primary-btn-outlined wt-push-nudge-modal-btn"
                onClick={handleDismiss}
              >
                Not now
              </button>
            </div>
            {error && (
              <p className="wt-push-nudge-modal-error" role="alert">
                {error}
              </p>
            )}
            <p className="wt-push-nudge-modal-footnote">
              You can change this later from the bell menu.
            </p>
          </>
        )}

        {variant === "ios" && (
          <>
            <h2 id={ENABLE_TITLE_ID} className="wt-push-nudge-modal-sr-only">
              {ENABLE_DIALOG_NAME}
            </h2>
            <div className="wt-push-nudge-modal-icon">
              <BellIcon />
            </div>
            <p className="wt-push-nudge-modal-title">
              Get WebTime alerts on your iPad or iPhone
            </p>
            <ol className="wt-push-nudge-modal-steps">
              <li>Tap the Share button (the square with an arrow) in Safari.</li>
              <li>Choose &quot;Add to Home Screen&quot;, then tap Add.</li>
              <li>Open WebTime from your Home Screen and log in.</li>
              <li>Tap Enable notifications when asked.</li>
            </ol>
            <p className="wt-push-nudge-modal-note">
              Needs iOS 16.4 or later. Notifications only work when WebTime is opened from
              the Home Screen.
            </p>
            <div className="wt-push-nudge-modal-actions">
              <button
                type="button"
                className="primary-btn-outlined wt-push-nudge-modal-btn"
                onClick={handleDismiss}
              >
                Got it
              </button>
            </div>
            <p className="wt-push-nudge-modal-footnote">
              You can change this later from the bell menu.
            </p>
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default PushNudgeBanner;
